import React from 'react';
import { Tooltip, makeStyles, shorthands, tokens } from "@fluentui/react-components";

const useChartStyles = makeStyles({
    chartContainer: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        ...shorthands.gap('0.625rem'),
    },
    barsContainer: {
        display: 'flex',
        flexDirection: 'row',
        ...shorthands.gap('1.25rem'),
        ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke1),
        paddingBottom: '0.625rem',
        minHeight: '12.5rem',
        alignItems: 'stretch',
    },
    groupContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        ...shorthands.gap('0.3125rem'),
        flex: 1,
        height: '100%',
        justifyContent: 'flex-end',
    },
    barsGroup: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-end',
        justifyContent: 'center',
        ...shorthands.gap('0.25rem'),
        flexGrow: 1,
        minHeight: '0.625rem',
        width: '100%',
    },
    bar: {
        ...shorthands.borderRadius('0.125rem'),
        cursor: 'pointer',
        transition: 'opacity 0.2s',
        ':hover': {
            opacity: 0.8,
        },
        minHeight: '1.5rem',
        flex: 1,
        minWidth: '1.5rem',
        ...shorthands.border('1px', 'solid', 'rgba(0,0,0,0.3)'),
    },
    label: {
        fontSize: '0.625rem',
        color: tokens.colorNeutralForeground1,
        maxWidth: '5rem',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    legendContainer: {
        display: 'flex',
        flexWrap: 'wrap',
        ...shorthands.gap('1rem'),
        marginTop: '0.625rem',
        justifyContent: 'center',
    },
    legendItem: {
        display: 'flex',
        alignItems: 'center',
        backgroundColor: 'transparent',
        border: 0,
        fontFamily: 'inherit',
        ...shorthands.gap('0.375rem'),
        fontSize: '0.75rem',
        color: tokens.colorNeutralForeground1,
        cursor: 'pointer',
        ...shorthands.padding('0.125rem', '0.25rem'),
        ...shorthands.borderRadius('0.25rem'),
        transition: 'background-color 0.2s',
        ':hover': {
            backgroundColor: tokens.colorNeutralBackground3,
        },
    },
    legendColorBox: {
        width: '0.75rem',
        height: '0.75rem',
        ...shorthands.borderRadius('0.125rem'),
    },
    tooltipContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        ...shorthands.gap('0.25rem'),
        padding: '0.25rem',
    },
    tooltipValue: {
        fontSize: '1.125rem',
        fontWeight: 'bold',
    },
    tooltipLabel: {
        fontSize: '0.75rem',
        fontWeight: 600,
    },
    tooltipSubtext: {
        fontSize: '0.625rem',
        color: tokens.colorNeutralForeground1,
    }
});

export interface IChartSeriesItem {
    key: string;
    data: number;
    legend: string;
    color: string;
    xAxisCalloutData?: string;
    yAxisCalloutData?: number;
    onClick?: () => void;
}

export interface IChartGroup {
    name: string;
    series: IChartSeriesItem[];
}

export interface SimpleGroupedBarChartProps {
    data: IChartGroup[];
    height?: number;
    barwidth?: number;
    chartTitle?: string;
    hideLabels?: boolean;
    chartLabel?: string;
    isCalloutForStack?: boolean;
    onLegendClick?: (legend: string, key: string) => void;
}

export const SimpleGroupedBarChart: React.FC<SimpleGroupedBarChartProps> = ({ 
    data, 
    height = 300, 
    barwidth = 20, 
    chartTitle,
    onLegendClick
}) => {
    const styles = useChartStyles();
    
    if (!data || data.length === 0) {
        return <div>No data available</div>;
    }

    // Find max value for scaling
    let maxValue = 0;
    data.forEach((group) => {
        group.series.forEach((item) => {
            if (item.data > maxValue) maxValue = item.data;
        });
    });
    
    if (maxValue === 0) maxValue = 10;
    else maxValue = maxValue * 1.1;

    // Extract unique legend items with their keys
    const uniqueLegends = Array.from(
        new Map(
            data.flatMap(g => g.series.map(s => [s.legend, { color: s.color, key: s.key }]))
        ).entries()
    ).map(([legend, { color, key }]) => ({ legend, color, key }));

    const handleLegendClick = (legend: string, key: string) => {
        if (onLegendClick) {
            onLegendClick(legend, key);
        }
    };

    return (
        <div className={styles.chartContainer} role="figure" aria-label={chartTitle ? `${chartTitle} bar chart` : 'Grouped bar chart'}>
            {chartTitle && <h3 style={{ fontWeight: 600, margin: 0, fontSize: '0.875rem', color: '#242424' }}>{chartTitle}</h3>}
            
            {/* Legend Section */}
            <div className={styles.legendContainer} role="list" aria-label={chartTitle ? `${chartTitle} legend` : 'Chart legend'}>
                {uniqueLegends.map((item) => onLegendClick ? (
                    <button
                        key={item.legend}
                        type="button"
                        className={styles.legendItem}
                        onClick={() => handleLegendClick(item.legend, item.key)}
                        title={`Click to filter by ${item.legend}`}
                        aria-label={`Filter by ${item.legend}`}
                    >
                        <div
                            className={styles.legendColorBox}
                            style={{ backgroundColor: item.color }}
                        />
                        <span>{item.legend}</span>
                    </button>
                ) : (
                    <div key={item.legend} className={styles.legendItem}>
                        <div
                            className={styles.legendColorBox}
                            style={{ backgroundColor: item.color }}
                        />
                        <span>{item.legend}</span>
                    </div>
                ))}
            </div>

            <div className={styles.barsContainer} style={{ height: height }}>
                {data.map((group, groupIndex) => (
                    <div key={groupIndex} className={styles.groupContainer}>
                        <div className={styles.barsGroup}>
                            {group.series.map((item, itemIndex) => {
                                const barHeight = (item.data / maxValue) * 100;
                                return (
                                    <Tooltip 
                                        key={itemIndex} 
                                        content={
                                            <div className={styles.tooltipContainer}>
                                                <span className={styles.tooltipLabel}>{item.legend}</span>
                                                <span className={styles.tooltipValue}>{item.yAxisCalloutData ?? item.data}</span>
                                                <span className={styles.tooltipSubtext}>{group.name}</span>
                                            </div>
                                        }
                                        relationship="description"
                                    >
                                        {item.onClick ? (
                                            <button
                                                type="button"
                                                className={styles.bar}
                                                style={{
                                                    height: `${barHeight}%`,
                                                    backgroundColor: item.color,
                                                    padding: 0,
                                                }}
                                                onClick={item.onClick}
                                                aria-label={`${item.legend}: ${item.data} in ${group.name}`}
                                            />
                                        ) : (
                                            <div
                                                className={styles.bar}
                                                style={{
                                                    height: `${barHeight}%`,
                                                    backgroundColor: item.color
                                                }}
                                                role="img"
                                                aria-label={`${item.legend}: ${item.data} in ${group.name}`}
                                            />
                                        )}
                                    </Tooltip>
                                );
                            })}
                        </div>
                        <div className={styles.label} title={group.name}>{group.name}</div>
                    </div>
                ))}
            </div>
        </div>
    );
};
